import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { type AccountingSoftware } from 'src/core/modules/bookkeep/integration/status';
import { rejectUnexpectedValue } from 'src/core/utils/switchGuard';

export function getAccountPayableSuppliersTitle(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return t(
        'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.title',
      );
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'PublicApi':
    case 'MicrosoftDynamics':
      return t(
        'bookkeep.integrations.settings.syncStatus.default.suppliers.title',
      );
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}

export function getAccountPayableSuppliersNotSyncedTitle(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return t(
        'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.notSyncedTitle',
      );
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'MicrosoftDynamics':
    case 'PublicApi':
      return t(
        'bookkeep.integrations.settings.syncStatus.default.suppliers.notSyncedTitle',
      );
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}

export function getAccountPayableSuppliersCalloutContent(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return {
        title: t(
          'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.calloutTitle',
        ),
        text: t(
          'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.mapVendorCalloutText',
        ),
      };
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'MicrosoftDynamics':
    case 'PublicApi':
      return {
        title: t(
          'bookkeep.integrations.settings.syncStatus.default.suppliers.calloutTitle',
        ),
        text: t(
          'bookkeep.integrations.settings.syncStatus.default.suppliers.mapVendorCalloutText',
        ),
      };
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}

export function getAccountPayableSupplierMapVendorMessage(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return {
        success: t(
          'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.mapVendorSuccessMessage',
        ),
        failure: t(
          'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.mapVendorFailureMessage',
        ),
      };
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'MicrosoftDynamics':
    case 'PublicApi':
      return {
        success: t(
          'bookkeep.integrations.settings.syncStatus.default.suppliers.mapVendorSuccessMessage',
        ),
        failure: t(
          'bookkeep.integrations.settings.syncStatus.default.suppliers.mapVendorFailureMessage',
        ),
      };
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}

export function getAccountPayableSupplierManualMappingError(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return {
        title: t(
          'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.manualMappingErrorHeader',
        ),
        text: t(
          'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.notAllowedManualMapping',
        ),
      };
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'MicrosoftDynamics':
    case 'PublicApi':
      return {
        title: t(
          'bookkeep.integrations.settings.syncStatus.default.suppliers.manualMappingErrorHeader',
        ),
        text: t(
          'bookkeep.integrations.settings.syncStatus.default.suppliers.notAllowedManualMapping',
        ),
      };
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}

export function getAccountPayableSuppliersMapVendorFormLabel(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return t(
        'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.mapVendorFormLabel',
      );
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'MicrosoftDynamics':
    case 'PublicApi':
      return t(
        'bookkeep.integrations.settings.syncStatus.default.suppliers.mapVendorFormLabel',
      );
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}

export function getAccountPayableSuppliersLeftLabel(
  integration: AccountingSoftware,
  t: TGlobalFunctionTyped,
) {
  switch (integration) {
    case 'Netsuite':
      return t(
        'bookkeep.integrations.settings.syncStatus.netsuite.suppliers.leftLabel',
      );
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'Quickbooks':
    case 'Cegid':
    case 'Datev':
    case 'Xero':
    case 'Sage':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'MicrosoftDynamics':
    case 'PublicApi':
      return t(
        'bookkeep.integrations.settings.syncStatus.default.suppliers.leftLabel',
      );
    default:
      return rejectUnexpectedValue('Unsupported integration:', integration);
  }
}
